import type { DataModelBuilderData } from "./dataModelBuilder.types";
import type { DataModel, GetDataModelsResponse } from "./dataModels.types";
import { SEED_CATALOG_TABLES, SEED_CATALOG_METRICS } from "./dataModelCatalog.mock";
import {
  normalizeDataModelSourceHandle,
  normalizeDataModelTargetHandle,
} from "./dataModelRelationEdges";

export type BuilderDocument = DataModelBuilderData & {
  createdAt: string;
  updatedAt: string;
  isDefault: boolean;
};

const normalizeBuilderRelations = (
  doc: BuilderDocument,
): BuilderDocument => ({
  ...doc,
  relations: doc.relations.map((relation) => ({
    ...relation,
    sourceHandle: relation.sourceHandle
      ? normalizeDataModelSourceHandle(relation.sourceHandle)
      : undefined,
    targetHandle: relation.targetHandle
      ? normalizeDataModelTargetHandle(relation.targetHandle)
      : undefined,
  })),
});

const nowIso = () => new Date().toISOString();

const seedOrdersBuilder = (): BuilderDocument => ({
  id: "orders",
  name: "Orders",
  description:
    "Customer-centric sales data model designed to unify orders, transactions, fulfillment, and revenue relationships across connected sources.",
  status: "unverified",
  version: 1,
  tables: [
    { tableId: "orders", x: 0, y: 40, expanded: false },
    { tableId: "customers", x: 280, y: 40, expanded: true },
  ],
  metricIds: ["gross-sales", "order-count"],
  relations: [
    {
      id: "rel_orders_customers",
      sourceTableId: "orders",
      targetTableId: "customers",
      joinType: "inner",
      cardinality: "many_to_one",
      columnMappings: [
        { sourceColumn: "customer_id", targetColumn: "customer_id" },
      ],
      sourceHandle: "source-right",
      targetHandle: "target-left",
    },
  ],
  tableSummaries: [
    {
      id: "orders",
      name: "Orders",
      tableType: "Fact",
      source: SEED_CATALOG_TABLES.find((t) => t.id === "orders")?.source,
    },
    {
      id: "customers",
      name: "Customers",
      tableType: "Dim",
      source: SEED_CATALOG_TABLES.find((t) => t.id === "customers")?.source,
    },
  ],
  metricSummaries: [
    { id: "gross-sales", name: "Gross sales", isAutoGenerated: true },
    { id: "order-count", name: "Order count", isAutoGenerated: false },
  ],
  isDefault: false,
  createdAt: "2025-02-01T00:00:00Z",
  updatedAt: "2025-02-15T00:00:00Z",
});

const seedListItems = (): DataModel[] => [
  {
    id: "default",
    name: "Default",
    description:
      "Auto-generated workspace-wide data model containing every connected table. Joins are inferred from primary key ↔ foreign key matches.",
    status: "verified",
    tablesCount: 34,
    metricsCount: 48,
    isDefault: true,
    createdAt: "2025-01-01T00:00:00Z",
    updatedAt: "2025-01-01T00:00:00Z",
  },
  {
    id: "orders",
    name: "Orders",
    description: seedOrdersBuilder().description,
    status: "unverified",
    tablesCount: 2,
    metricsCount: 2,
    isDefault: false,
    createdAt: "2025-02-01T00:00:00Z",
    updatedAt: "2025-02-15T00:00:00Z",
  },
  {
    id: "products",
    name: "Products",
    description:
      "Business-ready product data model built to organize catalog, pricing, inventory, and performance data for consistent reporting and analysis.",
    status: "verified",
    tablesCount: 1,
    metricsCount: 1,
    isDefault: false,
    createdAt: "2025-03-01T00:00:00Z",
    updatedAt: "2025-03-10T00:00:00Z",
  },
];

const seedBuilders = (): Map<string, BuilderDocument> => {
  const map = new Map<string, BuilderDocument>();
  map.set("orders", seedOrdersBuilder());
  map.set("products", {
    id: "products",
    name: "Products",
    description:
      "Business-ready product data model built to organize catalog, pricing, inventory, and performance data for consistent reporting and analysis.",
    status: "verified",
    version: 1,
    tables: [{ tableId: "products", x: 0, y: 40 }],
    metricIds: ["units-sold"],
    relations: [],
    isDefault: false,
    createdAt: "2025-03-01T00:00:00Z",
    updatedAt: "2025-03-10T00:00:00Z",
  });
  return map;
};

let listItems: DataModel[] = seedListItems();
let builders: Map<string, BuilderDocument> = seedBuilders();
let nextId = 1;

export const resetDataModelsMockStore = (): void => {
  listItems = seedListItems();
  builders = seedBuilders();
  nextId = 1;
};

export const getMockListItems = (): DataModel[] => [...listItems];

export const getMockListResponse = (
  items: DataModel[],
  limit = 50,
  offset = 0,
): GetDataModelsResponse => {
  const page = items.slice(offset, offset + limit);
  return {
    items: page,
    pagination: {
      total: items.length,
      limit,
      offset,
      hasMore: offset + page.length < items.length,
    },
  };
};

export const getMockBuilder = (id: string): BuilderDocument | undefined => {
  const doc = builders.get(id);
  return doc ? normalizeBuilderRelations(doc) : undefined;
};

export const setMockBuilder = (doc: BuilderDocument): void => {
  builders.set(doc.id, normalizeBuilderRelations(doc));
};

export const upsertMockListItem = (item: DataModel): void => {
  const index = listItems.findIndex((i) => i.id === item.id);
  if (index >= 0) listItems[index] = item;
  else listItems.push(item);
};

export const removeMockListItem = (id: string): boolean => {
  const item = listItems.find((i) => i.id === id);
  if (!item || item.isDefault) return false;
  listItems = listItems.filter((i) => i.id !== id);
  builders.delete(id);
  return true;
};

export const createMockId = (): string => {
  nextId += 1;
  return `dm_${Date.now()}_${nextId}`;
};

export const countTablesAndMetrics = (
  tables: { tableId: string }[],
  metricIds: string[],
): { tablesCount: number; metricsCount: number } => ({
  tablesCount: tables.length,
  metricsCount: metricIds.length,
});

/** Re-export catalog seeds for mocks. */
export { SEED_CATALOG_TABLES, SEED_CATALOG_METRICS };
